import {useState} from 'react';
import {Button} from '@material-ui/core';
import dynamic from 'next/dynamic';

const TrumbowygEditor = dynamic(() => import('./TrumbowygEditor'), {
    ssr: false,
});

// general emails
import generalTemplate from './templates/general';
import quoteTermNonblend from './templates/quote/term-nonblend';
import quoteIULNonblend from './templates/quote/iul-nonblend';
import quoteGINonblend from './templates/quote/gi-nonblend';
import quoteGULNonblend from './templates/quote/gul-nonblend';

// after sale emails nonblend
import afterSaleIULNonblend from './templates/afterSale/iul-afterSale-nonblend';
import afterSaleGULNonblend from './templates/afterSale/gul-afterSale-nonblend';
import afterSaleTermNonblend from './templates/afterSale/term-afterSale-nonblend';
import afterSaleGINonblend from './templates/afterSale/gi-afterSale-nonblend';
import afterSaleProsperIULNonblend from './templates/afterSale/prosper-iul-afterSale-nonblend';

// blended after sale emails
import afterSaleIULTermBlend from './templates/afterSale/iul-term-afterSale-blend';
import afterSaleGULTermBlend from './templates/afterSale/gul-term-afterSale-blend';

// blended emails
import quoteIULTermBlend from './templates/combinationQuote/iul-term-blend';
import quoteGULTermBlend from './templates/combinationQuote/gul-term-blend';

// family emails
import familyNonblend from './templates/family/family-gul-nonblend';
import afterSaleFamilyNonblend from './templates/afterSale/family-gul-afterSale-nonblend';

const GENERAL_BROCHURE =
    'https://amplify-strapi-production.s3.us-west-1.amazonaws.com/Amplify_Life_Insurance_Brochure_fcaeac9b05.pdf';
const NATIONAL_LIFE_BROCHURE =
    'https://amplify-strapi-production.s3.us-west-1.amazonaws.com/LSW_Living_Benefits_5622d92761.pdf';
const AMERITAS_TERM_BROCHURE =
    'https://amplify-strapi-production.s3.us-west-1.amazonaws.com/Ameritas_living_benefits_term_692769a791.pdf';
const AMERITAS_IUL_BROCHURE =
    'https://amplify-strapi-production.s3.us-west-1.amazonaws.com/Ameritas_Product_brochure_IUL_bdd233f129.pdf';
const AMERITAS_VUL_BROCHURE =
    'https://amplify-strapi-production.s3.us-west-1.amazonaws.com/Ameritas_Advisor_II_VUL_0e461a27e6.pdf';
const NORTH_AMERICAN_BROCHURE =
    'https://amplify-strapi-production.s3.us-west-1.amazonaws.com/North_American_Living_benefits_brochure_aef8d9b2dd.pdf';

export enum TemplateTypes {
    'general',
    'quoteTermNonblend',
    'quoteIulNonblend',
    'quoteLSWIULNonblend',
    'quoteGulNonblend',
    'quoteGuaranteedNonblend',
    'quoteVULNonblend',
    'quoteProsperIULNonblend',
    'quoteIULTermBlend',
    'quoteLSWIULTermBlend',
    'quoteGULTermBlend',
    'quoteVULTermBlend',
    'quoteFamily',
}

export enum TemplatePurposes {
    'quoted',
    'afterSale',
}

export enum TemplateDataFields {
    productName = 'productName',
    faceAmount = 'faceAmount',
    monthlyPremium = 'monthlyPremium',
    cashValue = 'cashValue',
    cashAge = 'cashAge',
    carrier = 'carrier',
    name = 'name',
    lifeCoverage = 'lifeCoverage',
    lifePremium = 'lifePremium',
    termPeriod = 'termPeriod',
    productType = 'productType',
    contactName = 'contactName',
    illustration = 'illustration',
    managerName = 'agent manager name',
}

export default function EmailEditor({
    onSend,
    templateType,
    templateData,
    emails,
    bccEmails,
    templatePurpose,
}: {
    onSend: (
        emails: string[],
        html: string,
        productType: string,
        templatePurpose: TemplatePurposes,
        bccEmails?: string[],
        subject?: string,
        illustration?: string,
    ) => void;
    templateType: TemplateTypes;
    templateData: any[];
    emails: string[];
    bccEmails?: string[];
    templatePurpose: TemplatePurposes;
}) {
    const [showSendButton, setShowSendButton] = useState(false);

    let renderFunc: any = generalTemplate;
    let subject: string | undefined;
    switch (templateType) {
        case TemplateTypes.quoteGuaranteedNonblend:
            renderFunc = quoteGINonblend;
            renderFunc = templatePurpose === TemplatePurposes.afterSale ? afterSaleGINonblend : renderFunc;
            break;
        case TemplateTypes.quoteVULNonblend:
        case TemplateTypes.quoteLSWIULNonblend:
        case TemplateTypes.quoteIulNonblend:
            renderFunc = quoteIULNonblend;
            renderFunc = templatePurpose === TemplatePurposes.afterSale ? afterSaleIULNonblend : renderFunc;
            break;
        case TemplateTypes.quoteProsperIULNonblend:
            renderFunc = quoteIULNonblend;
            renderFunc = templatePurpose === TemplatePurposes.afterSale ? afterSaleProsperIULNonblend : renderFunc;
            break;
        case TemplateTypes.quoteGulNonblend:
            renderFunc = quoteGULNonblend;
            renderFunc = templatePurpose === TemplatePurposes.afterSale ? afterSaleGULNonblend : renderFunc;
            break;
        case TemplateTypes.quoteTermNonblend:
            renderFunc = quoteTermNonblend;
            renderFunc = templatePurpose === TemplatePurposes.afterSale ? afterSaleTermNonblend : renderFunc;
            break;
        case TemplateTypes.quoteVULTermBlend:
        case TemplateTypes.quoteLSWIULTermBlend:
        case TemplateTypes.quoteIULTermBlend:
            renderFunc = quoteIULTermBlend;
            renderFunc = templatePurpose === TemplatePurposes.afterSale ? afterSaleIULTermBlend : renderFunc;
            break;
        case TemplateTypes.quoteGULTermBlend:
            renderFunc = quoteGULTermBlend;
            renderFunc = templatePurpose === TemplatePurposes.afterSale ? afterSaleGULTermBlend : renderFunc;
            break;
        case TemplateTypes.quoteFamily:
            renderFunc = familyNonblend;
            renderFunc = templatePurpose === TemplatePurposes.afterSale ? afterSaleFamilyNonblend : renderFunc;
            break;
        default:
            break;
    }

    if (templatePurpose === TemplatePurposes.afterSale) subject = 'Your life insurance application has been submitted!';

    let brochureLink = GENERAL_BROCHURE;
    if (/National Life Group/.test(templateData[0].carrier)) {
        brochureLink = NATIONAL_LIFE_BROCHURE;
    } else if (/North American/.test(templateData[0].carrier)) {
        brochureLink = NORTH_AMERICAN_BROCHURE;
    } else if (/Ameritas/.test(templateData[0].carrier)) {
        if ([TemplateTypes.quoteIulNonblend, TemplateTypes.quoteIULTermBlend].includes(templateType)) {
            brochureLink = AMERITAS_IUL_BROCHURE;
        } else if ([TemplateTypes.quoteVULNonblend, TemplateTypes.quoteVULTermBlend].includes(templateType)) {
            brochureLink = AMERITAS_VUL_BROCHURE;
        } else {
            brochureLink = AMERITAS_TERM_BROCHURE;
        }
    }

    const emailData = templateData?.map((data) => {
        return {
            name: data.name,
            contact_name: data.contactName,
            face_amount: data.faceAmount,
            cash_value: data.cashValue,
            cash_age: '70',
            monthly_premium: data.monthlyPremium,
            life_coverage: data.lifeCoverage,
            life_premium: data.lifePremium,
            term_period: data.termPeriod,
            product_type: data.productType,
            carrier_name: data.carrier,
            illnessSection: /Ameritas|National Life Group|North American/.test(data.carrier),
            brochureLink,
            template_purpose: templatePurpose,
            template_type: templateType,
            has_illustration: !!data.illustration,
        };
    });

    const initialValue = renderFunc(
        templateType !== TemplateTypes.quoteFamily
            ? {
                  name: templateData[0].name,
                  face_amount: templateData[0].faceAmount,
                  cash_value: templateData[0].cashValue,
                  cash_age: '70',
                  monthly_premium: templateData[0].monthlyPremium,
                  life_coverage: templateData[0].lifeCoverage,
                  life_premium: templateData[0].lifePremium,
                  term_period: templateData[0].termPeriod,
                  carrier_name: templateData[0].carrier,
                  illnessSection: /Ameritas|National Life Group|North American/.test(templateData[0].carrier),
                  brochureLink,
                  template_purpose: templatePurpose,
                  template_type: templateType,
                  has_illustration: !!templateData[0].illustration,
                  manager_name: templateData[0].managerName,
              }
            : emailData,
    );

    const [editorContent, setEditorContent] = useState(initialValue);

    const doSend = () => {
        const removedFormatting = removeFormatting(editorContent);
        onSend(
            emails,
            removedFormatting,
            templateData[0].productType,
            templatePurpose,
            bccEmails,
            subject,
            templateData[0].illustration,
        );
    };

    return (
        <>
            <TrumbowygEditor
                initialValue={initialValue}
                setShowSendButton={setShowSendButton}
                onChange={setEditorContent}
            />
            {showSendButton && !!emails?.length && (
                <div style={{display: 'flex', justifyContent: 'center', padding: 10}}>
                    <Button color="primary" onClick={doSend} variant="contained">
                        Send email
                    </Button>
                </div>
            )}
        </>
    );
}

export function removeFormatting(html: any) {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const wrappers = document.querySelectorAll('.wrapper');
    wrappers.forEach((node) => {
        node.replaceWith(node.textContent || '');
    });
    return document.body.innerHTML;
}
